import { filter, isEmpty, isEqual, map, some } from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import programLocationTreeQuery from '../../../../../common/data/ProgramGroups/programLocationTree.graphql';
import nodeSortingOptionsSequence from '../../../../utils/nodeSortingOptionsSequence';
import { isLevel } from '../../../containers/EntityForm/fields/RoleAssignmentField/RoleAssignmentField';
import useT from '../../../utils/Translations/useT';
import GqlStaticTreeSelector from '../GqlStaticTreeSelector';
import ProgramStructureTreeAdapter, { canRender, isProgramXOrgStructLevel, selectionFilter, } from '../../../adapters/ProgramStructureTreeAdapter';
import Statuses from '../../../../../model/Statuses';
import ProgramStructureSelectorTreeFilters from './ProgramStructureSelectorTreeFilters';
import Button from '../../Button';
import { isRootNode } from '../../../dataViews/DynamicTree';
import StatusAddon from '../../../dataViews/NextTree/addons/StatusAddon';
const ProgramStructureTreeSelector = ({ title = 'Programs', programGroupIds = [], organisations = [], disabled = false, onChange, value, isProgramGroupIdsRequired, initialSelectAll = false, name = 'programStructure', gqlVariables, plugins = {}, }) => {
    const t = useT();
    const initialStatus = Statuses.Active.value;
    const defaultFilter = {
        status: initialStatus,
        searchQuery: '',
    };
    const [filterValues, setFilterValues,] = useState(defaultFilter);
    const [allModels, setAllModels] = useState();
    const [filterItems, setFilterItems] = useState(undefined);
    useEffect(() => {
        if (!isEmpty(allModels) && !isEmpty(value)) {
            const filteredArray = filter(value, item1 => some(allModels, item2 => item1.id === item2.id && item1.__typename === item2.__typename));
            if (!isEmpty(filteredArray)) {
                const idsArray1 = map(filteredArray, 'id');
                const idsArray2 = map(value, 'id');
                const areArraysEqual = isEqual(idsArray1, idsArray2);
                if (!areArraysEqual) {
                    onChange && onChange(filteredArray);
                }
            }
            else {
                onChange && onChange([]);
            }
        }
    }, [allModels, value, onChange]);
    const levelIds = useMemo(() => map(filter(organisations, isLevel), 'id'), [
        organisations,
    ]);
    const _gqlSkip = useMemo(() => (isProgramGroupIdsRequired && isEmpty(programGroupIds)) ||
        isEmpty(levelIds), [isProgramGroupIdsRequired, programGroupIds, levelIds]);
    const _gqlVariables = useMemo(() => ({
        includeProgramGroups: true,
        programGroupIds: isEmpty(programGroupIds) && !isProgramGroupIdsRequired
            ? undefined
            : programGroupIds,
        levelIds,
        ...gqlVariables,
    }), [isProgramGroupIdsRequired, programGroupIds, levelIds, gqlVariables]);
    // useResetOnDeps<[]>(
    //   { programGroupIds, organisations },
    //   onChange,
    //   [],
    //   !initialSelectAll,
    // );
    useEffect(() => {
        if ((isEmpty(organisations) ||
            (isProgramGroupIdsRequired && isEmpty(programGroupIds))) &&
            !isEmpty(value)) {
            onChange && onChange([]);
        }
    }, [
        onChange,
        value,
        organisations,
        isProgramGroupIdsRequired,
        programGroupIds,
    ]);
    useEffect(() => {
        if (onChange && filterItems && !disabled) {
            const _filterItems = filterItems;
            onChange(_filterItems);
            setFilterItems(undefined);
        }
    }, [disabled, filterValues, onChange, filterItems]);
    const _onChange = useCallback(selectedItems => {
        const filtered = selectedItems.filter(node => node.intakeId !== null && node.status === filterValues.status);
        setFilterItems(filtered);
    }, [filterValues.status]);
    const nodeIsVisible = useCallback(node => isProgramXOrgStructLevel(node)
        ? node.intakeId !== null && node.status === filterValues.status
        : true, [filterValues.status]);
    const isAllSelected = useMemo(() => {
        if (allModels && (filterItems || value)) {
            const allCurrModelsLength = allModels === null || allModels === void 0 ? void 0 : allModels.filter(x => isProgramXOrgStructLevel(x) &&
                x.intakeId !== null &&
                x.status === filterValues.status).length;
            const filterItemLength = filterItems === null || filterItems === void 0 ? void 0 : filterItems.length;
            const valueLength = value === null || value === void 0 ? void 0 : value.length;
            return (allCurrModelsLength === filterItemLength ||
                allCurrModelsLength === valueLength);
        }
        return false;
    }, [allModels, filterItems, filterValues.status, value]);
    const renderNodeAddons = (node, meta) => {
        const { handleClear } = meta;
        const handleClearFn = e => {
            handleClear();
            e.preventDefault();
            e.stopPropagation();
            e.stopImmediatePropagation();
        };
        const addons = [];
        if (isRootNode(node)) {
            addons.push(<Button additionClasses="text-primary no-padding-top no-padding-bottom" buttonStyle="link" onClick={handleClearFn}>
          {t('Clear')}
        </Button>);
        }
        addons.push(<StatusAddon key={`status_${node.id}`} customActiveStatus={Statuses.Active} customDeletedStatus={Statuses.Deleted} customStatuses={Statuses.BasicByValue} status={node.status}/>);
        return addons;
    };
    return (<GqlStaticTreeSelector hasFilter hasSyntheticRootNode adapter={{ ...ProgramStructureTreeAdapter, canRender, renderNodeAddons }} disabled={_gqlSkip || disabled} filter={{
        component: ProgramStructureSelectorTreeFilters,
        value: filterValues,
        onChange: setFilterValues,
    }} gql={programLocationTreeQuery} gqlSkip={_gqlSkip} gqlVariables={_gqlVariables} hasClearButton={false} initialSelectAll={initialSelectAll} isAllSelected={isAllSelected} name={name} plugins={{
        nodeIsVisible,
        nodeSortingOptions: nodeSortingOptionsSequence,
    }} selectionFilter={selectionFilter} setAllModels={setAllModels} syntheticRootNodeName={t('Program Structure')} title={t(title)} tree="ProgramStructureTreeSelector" value={value} onChange={_onChange}/>);
};
export default ProgramStructureTreeSelector;
